/* Store - favourites ("bookmarks"), saved in the customer's browser only (no account, nothing sent).
 *
 * Storage: localStorage 'pl-wishlist' = [{ handle, id, addedAt }], newest first, shared by the tabs of the
 * browser (storage event) and kept across visits (for a limited time: browsers may clear site data). When the
 * browser blocks storage or a write fails, the list lives in memory for the current page.
 *
 * Markup driven here (every part optional on a page):
 *   [data-wishlist-toggle][data-product-handle][data-product-id]  bookmark buttons (snippets/wishlist-button.liquid):
 *                                                                 aria-pressed + .is-active follow the list (product
 *                                                                 id first, then handle: renamed products stay saved)
 *   [data-wishlist-count]          number of favourites           [data-wishlist-count-wrap]  hidden while 0
 *   html.rd-wishlist-filled        while the list is not empty (filled ribbon of the header basket icon)
 *   #WishlistConfig, #WishlistToastTemplate, [data-wishlist-live]  snippets/wishlist-assets.liquid
 *   [data-wishlist-page] > [data-wishlist-grid]  favourites page (sections/main-wishlist.liquid): one product card
 *                                  per favourite, fetched from /products/<handle>?view=wishlist-card
 *                                  (templates/product.wishlist-card.liquid, 6 requests at a time), cached for the
 *                                  session; the section prints placeholders before paint (adopted here) and
 *                                  window.PLWishlistLayout (staggered phone columns); failed cards show
 *                                  #WishlistCardError (bookmark + link + retry)
 * API: window.ShopifyWishlist { items(), has(handle, id), add(handle, id), remove(handle, id), toggle(handle, id) };
 * event 'wishlist:change' on document ({ detail: { items, handle, saved } }).
 */
(function () {
  'use strict';
  if (window.ShopifyWishlist) return;

  var KEY = 'pl-wishlist';
  var TOAST_SEEN = 'pl-wishlist-toast';
  var CARD_CACHE = 'pl-wishlist-cards';
  var CARD_TTL = 10 * 60 * 1000;
  var MAX_ITEMS = 100;
  var MAX_FETCHES = 6;
  var TOAST_MS = 6000;
  var HANDLE = (function () {
    try {
      return new RegExp('^[\\p{L}\\p{N}][\\p{L}\\p{N}_-]*$', 'u');
    } catch (error) {
      return /^[A-Za-z0-9][A-Za-z0-9_-]*$/;
    }
  })();

  var memory = [];
  var writeFailed = false;
  var config = readConfig();

  function readConfig() {
    var c = {};
    var el = document.getElementById('WishlistConfig');
    if (el) {
      try {
        c = JSON.parse(el.textContent) || {};
      } catch (error) {
        c = {};
      }
    }
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    c.productsUrl = c.productsUrl || root + 'products/';
    c.view = c.view || 'wishlist-card';
    c.toast = c.toast || 'first';
    return c;
  }

  /* ---- storage ------------------------------------------------------------------------------- */
  function validHandle(handle) {
    return typeof handle === 'string' && handle.length <= 255 && HANDLE.test(handle);
  }

  function clean(list) {
    var seen = {};
    var seenIds = {};
    var out = [];
    (Array.isArray(list) ? list : []).forEach(function (item) {
      if (!item || !validHandle(item.handle) || seen[item.handle]) return;
      var id = Number(item.id) || null;
      if (id && seenIds[id]) return;
      seen[item.handle] = true;
      if (id) seenIds[id] = true;
      out.push({ handle: item.handle, id: id, addedAt: Number(item.addedAt) || 0 });
    });
    return out.slice(0, MAX_ITEMS);
  }

  function read() {
    if (writeFailed) return memory.slice(); /* storage refuses our writes: this page's list is the truth */
    try {
      var raw = window.localStorage.getItem(KEY);
      memory = raw ? clean(JSON.parse(raw)) : [];
    } catch (error) {
      /* storage blocked or corrupted: keep the list of this page */
    }
    return memory.slice();
  }

  function write(list, detail) {
    memory = clean(list);
    try {
      window.localStorage.setItem(KEY, JSON.stringify(memory));
    } catch (error) {
      writeFailed = true; /* quota / blocked: keep the in-memory list for the rest of the visit */
    }
    changed(detail);
  }

  /* a saved entry matches a product by id first (handles change when a product is renamed), then by handle */
  function find(list, handle, id) {
    id = Number(id) || null;
    for (var i = 0; i < list.length; i++) if (id && list[i].id === id) return list[i];
    for (var j = 0; j < list.length; j++) if (list[j].handle === handle) return list[j];
    return null;
  }

  function has(handle, id) {
    return !!find(read(), handle, id);
  }

  function add(handle, id) {
    if (!validHandle(handle)) return;
    var list = read();
    var existing = find(list, handle, id);
    list = list.filter(function (item) {
      return item !== existing;
    });
    list.unshift({ handle: handle, id: Number(id) || null, addedAt: Date.now() });
    write(list, { handle: handle, saved: true });
  }

  function remove(handle, id) {
    var list = read();
    var existing = find(list, handle, id);
    if (!existing) return;
    write(
      list.filter(function (item) {
        return item !== existing;
      }),
      { handle: existing.handle, saved: false }
    );
  }

  function toggle(handle, id) {
    if (has(handle, id)) {
      remove(handle, id);
      return false;
    }
    add(handle, id);
    return true;
  }

  /* the product answered under another handle (renamed): keep the entry (and its date) under the new one */
  function rename(oldHandle, newHandle, id) {
    var list = read();
    var entry = find(list, oldHandle, null);
    if (!entry || !validHandle(newHandle)) return;
    if (find(list, newHandle, id) && find(list, newHandle, id) !== entry) {
      list = list.filter(function (item) {
        return item !== entry;
      });
    } else {
      entry.handle = newHandle;
      if (Number(id)) entry.id = Number(id);
    }
    write(list, { handle: newHandle, renamed: oldHandle });
  }

  /* an entry kept without its product id (older data, a renamed product found again under its new handle)
     takes the id of its card */
  function adoptId(handle, id) {
    id = Number(id) || null;
    if (!id) return;
    var list = read();
    var entry = find(list, handle, null);
    if (!entry || entry.id || find(list, '', id)) return;
    entry.id = id;
    write(list, { handle: handle, updated: true });
  }

  /* ---- page state ---------------------------------------------------------------------------- */
  function changed(detail) {
    sync(document);
    renderPage();
    document.dispatchEvent(
      new CustomEvent('wishlist:change', { detail: Object.assign({ items: memory.slice() }, detail || {}) })
    );
  }

  function sync(root) {
    var byHandle = {};
    var byId = {};
    memory.forEach(function (item) {
      byHandle[item.handle] = true;
      if (item.id) byId[item.id] = true;
    });
    var count = memory.length;
    var scope = root && root.querySelectorAll ? root : document;
    var buttons = scope.querySelectorAll('[data-wishlist-toggle]');
    for (var i = 0; i < buttons.length; i++) {
      var id = Number(buttons[i].getAttribute('data-product-id'));
      var on = (id && byId[id] === true) || byHandle[buttons[i].getAttribute('data-product-handle')] === true;
      if (buttons[i].getAttribute('aria-pressed') !== String(on)) buttons[i].setAttribute('aria-pressed', String(on));
      if (buttons[i].classList.contains('is-active') !== on) buttons[i].classList.toggle('is-active', on);
    }
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      if (el.textContent !== String(count)) el.textContent = String(count);
    });
    document.querySelectorAll('[data-wishlist-count-wrap]').forEach(function (el) {
      if (el.hidden !== (count === 0)) el.hidden = count === 0;
    });
    document.documentElement.classList.toggle('rd-wishlist-filled', count > 0);
  }

  /* ---- buttons ------------------------------------------------------------------------------- */
  var lastButton = null;

  document.addEventListener('click', function (event) {
    var button = event.target && event.target.closest ? event.target.closest('[data-wishlist-toggle]') : null;
    if (!button) return;
    event.preventDefault();
    var handle = button.getAttribute('data-product-handle');
    if (!validHandle(handle)) return;
    lastButton = button;
    if (toggle(handle, button.getAttribute('data-product-id'))) {
      /* detail 0 = Enter / Space (or a script): the message then waits for the customer */
      var shown = maybeToast(event.detail === 0);
      announce('added', shown ? toastSpeech() : '');
    } else {
      announce('removed');
    }
  });

  /* polite live region: the action, then the message's text when the message appears */
  function announce(kind, extra) {
    var live = document.querySelector('[data-wishlist-live]');
    if (!live) return;
    var text = live.getAttribute('data-' + kind) || '';
    if (extra) text = sentence(text) + ' ' + extra;
    live.textContent = '';
    window.setTimeout(function () {
      live.textContent = text;
    }, 60);
  }

  function sentence(text) {
    text = String(text || '')
      .replace(/\s+/g, ' ')
      .trim();
    return text && !/[.!?…]$/.test(text) ? text + '.' : text;
  }

  /* ---- message after saving (theme setting: first time of the visit / every time / never) --------- */
  var toast = null;
  var toastTimer = 0;
  var toastShownHere = false;
  var toastSticky = false;
  var toastOpen = false;
  /* layers that open over the page: the message closes when the focus moves into a new one */
  var LAYERS = '[role="dialog"], dialog, [aria-modal="true"], .drawer, .menu-drawer, quick-add-modal, modal-dialog';

  /* true when the message is shown */
  function maybeToast(fromKeyboard) {
    if (config.toast === 'never' || document.querySelector('[data-wishlist-page]')) return false;
    if (config.toast !== 'always') {
      try {
        if (window.sessionStorage.getItem(TOAST_SEEN)) return false;
        window.sessionStorage.setItem(TOAST_SEEN, '1');
      } catch (error) {
        if (toastShownHere) return false;
      }
    }
    toastShownHere = true;
    return showToast(fromKeyboard);
  }

  /* the message's title and text, for the live region */
  function toastSpeech() {
    if (!toast) return '';
    return Array.prototype.map
      .call(toast.querySelectorAll('.rd-wishlist-toast__title, .rd-wishlist-toast__text'), function (el) {
        return sentence(el.textContent);
      })
      .join(' ');
  }

  function onToastKey(event) {
    if (event.key === 'Escape' || event.key === 'Esc') hideToast();
  }

  /* the focus moves outside the message: into a new layer (popup, drawer, menu, search), the message closes;
     elsewhere it stops waiting (closes after 6 s) and never covers the focused element */
  function onToastFocus(event) {
    var target = event.target;
    if (!toastOpen || !target || !target.closest || toast.contains(target)) return;
    var layer = target.closest(LAYERS);
    if (layer && !(lastButton && layer.contains(lastButton))) {
      hideToast();
      return;
    }
    if (toastSticky) {
      toastSticky = false;
      scheduleToast();
    }
    window.requestAnimationFrame(function () {
      uncover(target);
    });
  }

  /* the message's box once in place (without its entrance shift) */
  function toastBox() {
    var rect = toast.getBoundingClientRect();
    var bottom = window.innerHeight - (parseFloat(window.getComputedStyle(toast).bottom) || 0);
    return { top: bottom - toast.offsetHeight, bottom: bottom, left: rect.left, right: rect.right };
  }

  /* an element under the message scrolls up above it; one that cannot move (fixed) closes the message */
  function uncover(el) {
    if (!toastOpen || !el || el === document.body || !el.getBoundingClientRect || toast.contains(el)) return;
    var box = toastBox();
    var covered = function () {
      var r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && r.bottom > box.top && r.top < box.bottom && r.right > box.left && r.left < box.right;
    };
    if (!covered()) return;
    var rootStyle = document.documentElement.style;
    var padding = rootStyle.scrollPaddingBottom;
    rootStyle.scrollPaddingBottom = window.innerHeight - box.top + 8 + 'px';
    try {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    } catch (error) {
      el.scrollIntoView(false);
    }
    rootStyle.scrollPaddingBottom = padding;
    if (covered()) hideToast();
  }

  function showToast(sticky) {
    var template = document.getElementById('WishlistToastTemplate');
    if (!template || !template.content || !template.content.firstElementChild) return false;
    if (!toast) {
      toast = template.content.firstElementChild.cloneNode(true);
      document.body.appendChild(toast);
      var close = toast.querySelector('[data-wishlist-toast-close]');
      if (close) close.addEventListener('click', hideToast);
      toast.addEventListener('mouseenter', pauseToast);
      toast.addEventListener('mouseleave', scheduleToast);
      toast.addEventListener('focusin', pauseToast);
      toast.addEventListener('focusout', function (event) {
        if (!toast.contains(event.relatedTarget)) scheduleToast();
      });
    }
    toastSticky = !!sticky;
    toastOpen = true;
    toast.hidden = false;
    document.addEventListener('keydown', onToastKey);
    document.addEventListener('focusin', onToastFocus);
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        if (!toastOpen) return;
        toast.classList.add('is-visible');
        /* saved from the keyboard: the focused bookmark stays visible above the message */
        if (toastSticky) uncover(document.activeElement);
      });
    });
    scheduleToast();
    return true;
  }

  function pauseToast() {
    window.clearTimeout(toastTimer);
  }

  function scheduleToast() {
    window.clearTimeout(toastTimer);
    if (!toastSticky) toastTimer = window.setTimeout(hideToast, TOAST_MS);
  }

  function hideToast() {
    if (!toast || !toastOpen) return;
    toastOpen = false;
    window.clearTimeout(toastTimer);
    document.removeEventListener('keydown', onToastKey);
    document.removeEventListener('focusin', onToastFocus);
    var hadFocus = toast.contains(document.activeElement);
    toast.classList.remove('is-visible');
    window.setTimeout(function () {
      if (!toastOpen) toast.hidden = true;
    }, 250);
    if (hadFocus && lastButton && lastButton.isConnected) lastButton.focus();
  }

  /* ---- favourites page ----------------------------------------------------------------------- */
  var cardRequests = {};

  function cacheName() {
    var s = window.Shopify || {};
    return CARD_CACHE + ':' + (s.locale || '') + ':' + ((s.currency && s.currency.active) || '') + ':' + (s.country || '');
  }

  function cacheGet(handle) {
    try {
      var all = JSON.parse(window.sessionStorage.getItem(cacheName()) || '{}');
      var entry = all[handle];
      if (entry && Date.now() - entry.t < CARD_TTL && entry.card) return entry.card;
    } catch (error) {}
    return null;
  }

  function cacheSet(handle, card) {
    try {
      var name = cacheName();
      var all = JSON.parse(window.sessionStorage.getItem(name) || '{}');
      all[handle] = { t: Date.now(), card: card };
      window.sessionStorage.setItem(name, JSON.stringify(all));
    } catch (error) {}
  }

  function productUrl(handle) {
    return config.productsUrl + encodeURIComponent(handle);
  }

  function cardUrl(handle) {
    return productUrl(handle) + '?view=' + encodeURIComponent(config.view);
  }

  function failure(kind) {
    var error = new Error('favourite card: ' + kind);
    error.kind = kind;
    return error;
  }

  function pathOf(url) {
    try {
      return new URL(url, window.location.href).pathname;
    } catch (error) {
      return String(url || '').split(/[?#]/)[0];
    }
  }

  /* the card template prints the card alone. Otherwise, after a redirect: to another product URL without ?view=
     (renamed product), fetch it again under that handle; to a page that is no product (the merchant redirected
     the URL of a deleted product, e.g. to a collection), the product is gone. Anything else is not a card. */
  function parseCard(text, response, handle) {
    var doc = new DOMParser().parseFromString(text, 'text/html');
    var first = doc.body && doc.body.firstElementChild;
    if (first && doc.body.children.length === 1 && first.classList.contains('card-wrapper')) {
      var toggleEl = first.querySelector('[data-wishlist-toggle]');
      return {
        html: first.outerHTML,
        handle: toggleEl ? toggleEl.getAttribute('data-product-handle') : null,
        id: toggleEl ? Number(toggleEl.getAttribute('data-product-id')) || null : null,
        redirected: !!response.redirected,
      };
    }
    if (response.redirected) {
      var path = pathOf(response.url);
      var moved = /\/products\/([^/?#]+)/.exec(path);
      if (moved) {
        var movedHandle = null;
        try {
          movedHandle = decodeURIComponent(moved[1]);
        } catch (error) {}
        if (validHandle(movedHandle) && movedHandle !== handle) {
          return { html: '', handle: movedHandle, id: null, redirected: true, refetch: true };
        }
      } else if (!/\/(?:account|password|challenge|checkpoint)(?:\/|$)/.test(path)) {
        throw failure('gone'); /* sign-in, password and bot-check pages say nothing about the product */
      }
    }
    throw failure('format');
  }

  /* at most MAX_FETCHES card requests at a time (a long list does not flood the store) */
  var fetching = 0;
  var fetchQueue = [];

  function limited(start) {
    return new Promise(function (resolve, reject) {
      var run = function () {
        fetching++;
        Promise.resolve()
          .then(start)
          .then(resolve, reject)
          .then(function () {
            fetching--;
            var next = fetchQueue.shift();
            if (next) next();
          });
      };
      if (fetching < MAX_FETCHES) run();
      else fetchQueue.push(run);
    });
  }

  function fetchCard(handle) {
    if (cardRequests[handle]) return cardRequests[handle];
    var cached = cacheGet(handle);
    if (cached) return (cardRequests[handle] = Promise.resolve(cached));
    cardRequests[handle] = limited(function () {
      return window.fetch(cardUrl(handle), { credentials: 'same-origin' }).then(function (response) {
        if (response.status === 404) throw failure('gone');
        if (!response.ok) throw failure('http');
        return response.text().then(function (text) {
          return parseCard(text, response, handle);
        });
      });
    })
      .then(function (card) {
        if (!card.refetch) cacheSet(handle, card);
        return card;
      })
      .catch(function (error) {
        delete cardRequests[handle];
        throw error;
      });
    return cardRequests[handle];
  }

  function cloneTemplate(id) {
    var template = document.getElementById(id);
    return template && template.content ? template.content.cloneNode(true) : null;
  }

  /* a card's quick add popup moves itself to <body> when it connects (theme modals): it leaves with its card,
     so a later card of the same product does not repeat its ids */
  function dropPopups(li) {
    var openers = li.querySelectorAll('modal-opener[data-modal]');
    for (var i = 0; i < openers.length; i++) {
      var modal = null;
      try {
        modal = document.querySelector(openers[i].getAttribute('data-modal'));
      } catch (error) {}
      if (!modal || li.contains(modal)) continue;
      if (modal.hasAttribute('open') && typeof modal.hide === 'function') modal.hide();
      if (modal.parentNode) modal.parentNode.removeChild(modal);
    }
  }

  function setLoading(li) {
    dropPopups(li);
    li.className = 'grid__item rd-wishlist__item is-loading';
    li.setAttribute('data-wishlist-state', 'loading');
    var skeleton = cloneTemplate('WishlistCardSkeleton');
    li.textContent = '';
    if (skeleton) li.appendChild(skeleton);
  }

  function newItem(handle) {
    var li = document.createElement('li');
    li.setAttribute('data-handle', handle);
    setLoading(li);
    return li;
  }

  /* the product's name while its card cannot be shown: its handle in words */
  function readable(handle) {
    var words = String(handle).replace(/[-_]+/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  function showError(li, handle) {
    dropPopups(li);
    li.className = 'grid__item rd-wishlist__item is-error';
    li.setAttribute('data-wishlist-state', 'error');
    li.textContent = '';
    var error = cloneTemplate('WishlistCardError');
    if (error) li.appendChild(error);
    var link = li.querySelector('[data-wishlist-error-link]');
    if (link) link.setAttribute('href', productUrl(handle));
    /* its bookmark removes the favourite (e.g. a product that will not come back) */
    var bookmark = li.querySelector('[data-wishlist-toggle]');
    if (bookmark) {
      var entry = find(memory, handle, null);
      bookmark.setAttribute('data-product-handle', handle);
      if (entry && entry.id) bookmark.setAttribute('data-product-id', String(entry.id));
      bookmark.setAttribute('aria-label', (bookmark.getAttribute('aria-label') || '').replace('[product]', readable(handle)));
    }
    var retry = li.querySelector('[data-wishlist-retry]');
    if (retry) {
      retry.addEventListener('click', function () {
        /* from the keyboard: the focus waits on the card, then goes to its bookmark (or this button again) */
        var keep = li.contains(document.activeElement);
        setLoading(li);
        if (keep) {
          li.setAttribute('tabindex', '-1');
          li.focus({ preventScroll: true });
        }
        layout();
        load(li, handle, keep);
      });
    }
    sync(li);
  }

  function settleFocus(li, focusAfter, target) {
    if ((focusAfter || document.activeElement === li) && target) target.focus();
    li.removeAttribute('tabindex');
  }

  function load(li, handle, focusAfter) {
    li.setAttribute('data-wishlist-state', 'requested');
    var item = find(memory, handle, null) || { handle: handle, id: null };
    fetchCard(handle).then(
      function (card) {
        if (!li.isConnected) return;
        if (card.handle && card.handle !== handle) {
          if ((item.id && card.id && item.id === card.id) || card.redirected) {
            li.setAttribute('data-handle', card.handle);
            if (!card.refetch) {
              cacheSet(card.handle, card);
              cardRequests[card.handle] = Promise.resolve(card);
            } else {
              setLoading(li);
            }
            rename(handle, card.handle, card.id);
            if (card.refetch) return;
          } else {
            remove(handle); /* another product answers under this handle: the saved one is gone */
            return;
          }
        }
        if (card.id) adoptId(card.handle || handle, card.id);
        dropPopups(li);
        li.innerHTML = card.html;
        li.className = 'grid__item rd-wishlist__item';
        li.setAttribute('data-wishlist-state', 'loaded');
        sync(li);
        layout();
        settleFocus(li, focusAfter, focusTarget(li));
      },
      function (error) {
        if (error && error.kind === 'gone') {
          remove(handle); /* deleted or unpublished product, or its URL now leads to another page */
        } else if (li.isConnected) {
          showError(li, handle);
          layout();
          settleFocus(li, focusAfter, li.querySelector('[data-wishlist-retry]'));
        }
      }
    );
  }

  function itemFor(grid, handle) {
    for (var i = 0; i < grid.children.length; i++) {
      if (grid.children[i].getAttribute('data-handle') === handle) return grid.children[i];
    }
    return null;
  }

  function focusTarget(li) {
    return li ? li.querySelector('[data-wishlist-toggle]') || li.querySelector('a, button') : null;
  }

  function renderPage() {
    var page = document.querySelector('[data-wishlist-page]');
    var grid = page && page.querySelector('[data-wishlist-grid]');
    if (!grid) return;
    var wanted = {};
    memory.forEach(function (item) {
      wanted[item.handle] = true;
    });
    var children = Array.prototype.slice.call(grid.children);
    var leaving = children.filter(function (li) {
      return !wanted[li.getAttribute('data-handle')];
    });
    leaving.forEach(function (li) {
      if (li.contains(document.activeElement)) {
        /* keyboard removal: focus the next card's bookmark, else the previous one, else the page title */
        var index = children.indexOf(li);
        var next = null;
        for (var n = index + 1; n < children.length && !next; n++) if (leaving.indexOf(children[n]) < 0) next = focusTarget(children[n]);
        for (var p = index - 1; p >= 0 && !next; p--) if (leaving.indexOf(children[p]) < 0) next = focusTarget(children[p]);
        if (!next) next = page.querySelector('[data-wishlist-title]');
        if (next) next.focus();
      }
      dropPopups(li);
      li.remove();
    });
    memory.forEach(function (item, index) {
      var li = itemFor(grid, item.handle) || newItem(item.handle);
      if (grid.children[index] !== li) grid.insertBefore(li, grid.children[index] || null);
      if (li.getAttribute('data-wishlist-state') === 'loading') load(li, item.handle);
    });
    page.classList.toggle('is-empty', memory.length === 0);
    layout();
  }

  /* staggered phone columns: the section's layout function (inline, so it also runs before this script) */
  var layoutQueued = false;
  function layout() {
    if (layoutQueued) return;
    layoutQueued = true;
    window.requestAnimationFrame(function () {
      layoutQueued = false;
      var grid = document.querySelector('[data-wishlist-page] [data-wishlist-grid]');
      if (grid && typeof window.PLWishlistLayout === 'function') window.PLWishlistLayout(grid);
    });
  }

  function watchLayout() {
    var grid = document.querySelector('[data-wishlist-page] [data-wishlist-grid]');
    if (!grid) return;
    var width = grid.getBoundingClientRect().width;
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () {
        var w = grid.getBoundingClientRect().width;
        if (Math.abs(w - width) > 0.5) {
          width = w;
          layout();
        }
      }).observe(grid);
    } else {
      window.addEventListener('resize', layout);
    }
  }

  /* the tab bar of the favourites page stays on the header's bottom edge (sticky / hidden header) */
  function followHeader() {
    if (!document.querySelector('[data-wishlist-page]')) return;
    var rootStyle = document.documentElement.style;
    var frames = 0;
    var update = function () {
      var header = document.querySelector('.section-header');
      var bottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      rootStyle.setProperty('--rd-wishlist-tabs-top', bottom + 'px');
    };
    var follow = function () {
      update();
      if (frames-- > 0) window.requestAnimationFrame(follow);
    };
    var kick = function () {
      var idle = frames <= 0;
      frames = 20;
      if (idle) window.requestAnimationFrame(follow);
    };
    window.addEventListener('scroll', kick, { passive: true });
    window.addEventListener('resize', kick);
    update();
  }

  /* ---- start --------------------------------------------------------------------------------- */
  function init() {
    read();
    var early = document.getElementById('WishlistEarlyStyle');
    if (early) early.remove();
    sync(document);
    renderPage();
    followHeader();
    watchLayout();
  }

  /* buttons added later (filters, recommendations, search, quick add, infinite scroll, editor) */
  var pending = false;
  new MutationObserver(function (mutations) {
    if (pending) return;
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes.length) {
        pending = true;
        window.requestAnimationFrame(function () {
          pending = false;
          sync(document);
        });
        return;
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* another tab of the browser changed the list */
  window.addEventListener('storage', function (event) {
    if (writeFailed || (event.key !== KEY && event.key !== null)) return;
    read();
    sync(document);
    renderPage();
  });

  /* back / forward cache: the list may have changed on another page */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    read();
    sync(document);
    renderPage();
  });

  document.addEventListener('shopify:section:load', function () {
    read();
    sync(document);
    renderPage();
  });

  window.ShopifyWishlist = {
    items: function () {
      return read();
    },
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
